import { requirementForPath } from 'constants/navPermissions';
import { RBAC_ENABLED, HIDE_DENIED_ACTIONS } from 'constants/rbacConfig';
import { describeModuleKey } from 'constants/moduleLabels';

/**
 * Per-ACTION permission for the screen you are already on.
 *
 * The route guard answers "may this person open this page". This answers the next
 * question down — may they create, edit, delete, export, approve, reject, cancel
 * on it — so a viewer sees the data without the buttons that change it.
 *
 * ── Why it derives the module from the ROUTE ────────────────────────────────
 *
 * A page could name its own module (`perms.can('admin', 'Revenue', 'Deal Codes',
 * 'create')`) and one does today. Twenty-five pages doing that is twenty-five
 * chances to type a module string that matches nothing — which fails SILENTLY, as
 * "no permission", hiding a button from people who hold it. Reading the module out
 * of constants/navPermissions.js instead means the buttons on a page are gated by
 * the same entry that decides whether the page opens at all. They cannot disagree,
 * and a page needs no permission knowledge of its own.
 *
 * For the two-requirement entries (Users & Roles' tabs, the Reports naming
 * collision) an action is allowed if ANY of the alternatives grants it — the same
 * rule the route guard uses, so "the page opened" and "the button works" stay
 * consistent.
 *
 * ── Fail open, deliberately ─────────────────────────────────────────────────
 *
 * An empty permission list, an unmapped path, or the master switch off all return
 * "allowed" for every action, matching the route guard and the sidebar. A session
 * that predates permissions in the login response must not turn every button on
 * every screen into a dead control.
 *
 * ⚠️ Affordance, not enforcement. Hiding a Delete button stops a mistake, not an
 * attacker; the write is authorized server-side or it is not authorized at all.
 */

/** The action vocabulary, mirroring the backend's PERMISSION_ACTIONS. */
export const ACTIONS = ['view', 'create', 'edit', 'delete', 'export', 'approve', 'reject', 'cancel'];

/**
 * Build the action checker for `pathname` from a bound permission checker.
 *
 * Kept as a plain function rather than a hook so the two portals can each wrap it
 * with their own auth source (React context for admin, a zustand store for
 * partner) without this logic existing twice.
 */
export const pageActionsFor = (perms, pathname, system) => {
  const ungated = !RBAC_ENABLED || !perms || !perms.list?.length;
  const requirement = ungated ? undefined : requirementForPath(pathname);
  const options = Array.isArray(requirement) ? requirement : requirement ? [requirement] : [];

  /** Is `action` granted under any of this screen's modules? */
  const can = (action) => {
    if (ungated || !options.length) return true;
    return options.some(({ side, module, subModule }) => perms.can(side, module, subModule || null, action));
  };

  return {
    can,
    canView: can('view'),
    canCreate: can('create'),
    canEdit: can('edit'),
    canDelete: can('delete'),
    canExport: can('export'),
    canApprove: can('approve'),
    canReject: can('reject'),
    canCancel: can('cancel'),

    /**
     * True when this screen is gated at all — i.e. the checks above are real
     * answers rather than the fail-open default.
     *
     * A page needs this to word a disabled control honestly: "your role does not
     * include this" is wrong if nothing was actually checked.
     */
    gated: !ungated && options.length > 0,

    /**
     * Which portal is asking, so `deniedTooltip` can name the module in THAT
     * sidebar's words — the partner portal calls Post-Booking Servicing
     * "Requests" and Promo Codes "Promotions". Undefined means the admin
     * portal, which is also the fallback wording.
     */
    system,

    /**
     * The module string(s) behind these answers, for a tooltip or error copy that
     * tells the operator which grant to ask for. Empty when ungated.
     */
    modules: options.map(({ side, module, subModule }) =>
      subModule ? `${side}:${module}/${subModule}` : `${side}:${module}`,
    ),
  };
};

/**
 * Standard tooltip for a control this role cannot use.
 *
 * One sentence, used everywhere, so a disabled button never leaves the operator
 * guessing whether it is broken or forbidden. Returns '' when the control is
 * enabled, because an empty title renders no tooltip at all — call sites that
 * carry their own hint ("Edit", "Delete") rely on that to keep it.
 *
 * The module is named in the SIDEBAR's words, not the catalogue's, so the thing
 * an operator is told to ask for is the thing the administrator will actually
 * find in the role editor.
 */
export const deniedTooltip = (allowed, actions, verb) => {
  if (allowed) return '';
  const named = (actions?.modules || []).map((key) => describeModuleKey(key, actions?.system)).join(' or ');
  return `Your role does not include permission to ${verb} here` + (named ? ` (${named})` : '');
};

/**
 * True when a denied control should be removed from the page rather than
 * disabled — the `REACT_APP_HIDE_DENIED_ACTIONS` deployment choice.
 *
 * Re-exported here so a call site needs one import to gate a control, and so
 * nothing outside constants/rbacConfig.js reads `process.env` for itself.
 */
export const hideDeniedActions = () => HIDE_DENIED_ACTIONS;

/**
 * Props for a control whose framework gives no JSX slot to wrap — antd's
 * `okButtonProps` being the case that forced this to exist.
 *
 * Returns the empty object when allowed, so spreading it is always safe:
 *
 *   okButtonProps={{ loading: saving, ...deniedButtonProps(canSave, actions, 'save') }}
 *
 * In hide mode it collapses the button with `display: none` rather than removing
 * it, because these APIs render the button themselves and there is nothing to
 * omit. The button is disabled in that branch too — a hidden-but-live control
 * would still be reachable by keyboard.
 */
export const deniedButtonProps = (allowed, actions, verb) => {
  if (allowed) return {};
  if (HIDE_DENIED_ACTIONS) return { disabled: true, style: { display: 'none' } };
  return { disabled: true, title: deniedTooltip(false, actions, verb) };
};
